<template>

    <template v-if="isMedia && hasMedia">

        <ThemeChanger :to="sectionTheme" :background="defaultBackground ?? data?.background_color">

            <Container fluid class="mt-32 md:mt-48">

                <Grid v-if="data?.carousel">

                    <Column :span="[ 2, 6 ]">

                        <Slider :images="data.assets" :sizes="sizes" :zoom-on-hover="data?.zoom_on_hover" :default-gradient="defaultGradient"/>

                    </Column>

                </Grid>

                <div v-else>

                    <AOS type="fade-up" easing="ease-in" duration="300" offset="-1000">

                        <template v-for="(media, index) of data.assets" :key="index">

                            <!-- <BlockRevealAnimation> -->

                                <Grid v-if="data.style.value === 'grid'">

                                    <LoopingVideo
                                        v-if="isVideoType(media.desktop_asset.mime_type)"
                                        :default-gradient="defaultGradient"
                                        :source="media.desktop_asset.permalink"
                                        class="border-none"
                                        :cover="media.video_cover"/>

                                    <Picture class="border-none" auto cover :image="media" :default-gradient="defaultGradient"/>

                                </Grid>

                                <div v-else class="w-screen">

                                    <LoopingVideo
                                        v-if="isVideoType(media.desktop_asset.mime_type)"
                                        :source="media.desktop_asset.permalink"
                                        :cover="media.video_cover"
                                        :default-gradient="defaultGradient"
                                        class="border-none"
                                        full-width/>

                                    <Picture class="border-none" :image="media" width="100vw" maxWidth="100vw" height="auto" maxHeight="100%" :default-gradient="defaultGradient"/>

                                </div>

                            <!-- </BlockRevealAnimation> -->

                        </template>

                    </AOS>

                </div>

            </Container>

        </ThemeChanger>

    </template>

    <template v-else>

        <ThemeChanger :to="sectionTheme" :background="defaultBackground ?? data?.background_color">

            <Container fluid :class="padding">

                <!-- <AOS type="fade-up" duration="1200" delay="400"> -->

                    <Grid>

                        <Column :span="[ 2, 4 ]" :push="[ 0, 1 ]">

                            <Heading tag="h2" v-if="data.heading" v-html="data.heading"/>

                            <Paragraph v-html="data.content"/>

                        </Column>

                    </Grid>

                <!-- </AOS> -->

                <div v-if="hasExtensions">

                    <template v-for="(extension, index) of data.extensions" :key="index">

                        <TitleAndList v-if="extension.type === 'list'" :title="extension.label" :list="extension.items" :key="`${extension.label}.${index}`" class="pt-16 md:pt-24"/>

                        <Checklist v-if="extension.type === 'checklist'" :data="extension.checklist" :with-icon="extension.include_check_icon" class="pt-16 md:pt-24"/>

                        <Testimonial v-if="extension.type === 'testimonial'" :data="extension" class="pt-16 md:pt-24"/>

                    </template>

                </div>

            </Container>

        </ThemeChanger>

    </template>

</template>

<script lang="ts">
    import { defineComponent, computed, PropType } from 'vue'
    import Column from './Grid/Column.vue'
    import Grid from './Grid/Grid.vue'
    // import BlockRevealAnimation from './BlockRevealAnimation.vue'
    import Container from './Container.vue'
    import Heading from './Heading.vue'
    import Paragraph from './Paragraph.vue'
    import LoopingVideo from './LoopingVideo.vue'
    import Picture from './Picture.vue'
    import TitleAndList from './TitleAndList.vue'
    import ThemeChanger from './ThemeChanger.vue'
    import Checklist from './Checklist.vue'
    import Testimonial from './Testimonial.vue'
    import Slider from './Slider.vue'
    import { isVideoType } from '../utils'
    import { Theme } from '../enums/Theme'
    import { useCarouselSizes } from '../compositions/UseCarouselSizes'

    export default defineComponent({
        name: 'BodyContent',
        components: {
            // BlockRevealAnimation,
            Container,
            Column,
            Grid,
            Slider,
            Heading,
            Paragraph,
            Picture,
            LoopingVideo,
            Checklist,
            TitleAndList,
            ThemeChanger,
            Testimonial
        },
        props: {
            data: { type: Object, required: true },
            usesCustomThemes: { type: Boolean, default: false },
            singleSpace: { type: Boolean, default: false },
            isLast: { type: Boolean, default: false },
            defaultBackground: { type: String, default: null },
            defaultGradient: { type: String, default: null },
            fallbackTheme: { type: String as PropType<Theme>, default: Theme.Default }
        },
        setup(props) {

            return {
                Theme,
                isVideoType,
                padding: computed(() => {

                    // 'py-32 md:py-48' : 'py-16 md:py-24'
                    if (props.isLast) {

                        return props.usesCustomThemes ? 'py-32 md:py-48' : 'py-16 md:py-24'

                    }

                    if (props.singleSpace) {

                        return 'pt-32 md:pt-48'

                    }

                    return props.usesCustomThemes ? 'pt-32 md:pt-48' : 'pt-16 md:pt-24'

                }),
                sectionTheme: computed(() => {

                    const themes: Record<string, string> = {
                        light: Theme.Default,
                        dark: Theme.Dark
                    }

                    return props.usesCustomThemes ? (themes[props.data.theme.value] ?? props.fallbackTheme) : props.fallbackTheme

                }),
                isMedia: computed(() => props.data.type === 'media'),
                hasMedia: computed(() => props.data.assets && props.data.assets.length > 0),
                hasExtensions: computed(() => props.data.extensions && props.data.extensions.length > 0),
                sizes: computed(() => useCarouselSizes(props.data.assets))
            }

        }
    })
</script>
